import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createAxiosInstance } from '../api/ApiRequest';
import { BotServerPublic, MonitoringConfig } from '../types/botServer';
import { useAuth } from './AuthContext';
import { SESSION_KEYS } from '../utils/sessionKeys';
import { buildAuthenticatedWebSocketUrl } from '../utils/websocketUrl';

interface ServerContextType {
    servers: BotServerPublic[];
    selectedServer: BotServerPublic | null;
    selectedServerId: string;
    setSelectedServerId: (serverId: string) => void;
    loading: boolean;
    refreshConfig: (preferFirst?: boolean) => Promise<void>;
    supportRefreshInterval: number;
    websocketUrl: string;
    authenticatedWebSocketUrl: string;
}

const ServerContext = createContext<ServerContextType>({
    servers: [],
    selectedServer: null,
    selectedServerId: '',
    setSelectedServerId: () => {},
    loading: true,
    refreshConfig: async () => {},
    supportRefreshInterval: 10,
    websocketUrl: '',
    authenticatedWebSocketUrl: '',
});

export const ServerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, logout } = useAuth();
    const axiosInstance = useMemo(() => createAxiosInstance(navigate, logout), [navigate, logout]);
    const [servers, setServers] = useState<BotServerPublic[]>([]);
    const [selectedServerId, setSelectedServerIdState] = useState<string>(() =>
        sessionStorage.getItem(SESSION_KEYS.SELECTED_SERVER_ID) || ''
    );
    const [loading, setLoading] = useState(true);
    const [supportRefreshInterval, setSupportRefreshInterval] = useState(10);
    const [websocketUrl, setWebsocketUrl] = useState('');
    const [wsAuthToken, setWsAuthToken] = useState('');

    const authenticatedWebSocketUrl = useMemo(
        () => buildAuthenticatedWebSocketUrl(websocketUrl, wsAuthToken),
        [websocketUrl, wsAuthToken]
    );

    const applyDefaultSelection = useCallback((nextServers: BotServerPublic[], preferFirst = false) => {
        if (nextServers.length === 0) {
            setSelectedServerIdState('');
            sessionStorage.removeItem(SESSION_KEYS.SELECTED_SERVER_ID);
            return;
        }

        const stored = sessionStorage.getItem(SESSION_KEYS.SELECTED_SERVER_ID);
        const validStored = !preferFirst && stored && nextServers.some((s) => s.serverId === stored);

        const nextId = validStored && stored ? stored : nextServers[0].serverId;
        setSelectedServerIdState(nextId);
        sessionStorage.setItem(SESSION_KEYS.SELECTED_SERVER_ID, nextId);
    }, []);

    const refreshConfig = useCallback(async (preferFirst = false) => {
        if (!isAuthenticated) {
            setServers([]);
            setSelectedServerIdState('');
            setWebsocketUrl('');
            setWsAuthToken('');
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const res = await axiosInstance.post<{ flag: boolean; servers: BotServerPublic[]; supportRefreshInterval: number; websocketUrl?: string; wsAuthToken?: string }>(
                '/api/v1/get_monitoring_config'
            );
            if (res.data?.flag) {
                const config = res.data as MonitoringConfig & { flag: boolean };
                const nextServers = (config.servers ?? []).filter((s) => s.serverId);
                setServers(nextServers);
                setSupportRefreshInterval(config.supportRefreshInterval ?? 10);
                setWebsocketUrl(config.websocketUrl ?? '');
                setWsAuthToken(config.wsAuthToken ?? '');
                applyDefaultSelection(nextServers, preferFirst);
            }
        } catch (err) {
            console.error('Failed to load monitoring config', err);
        } finally {
            setLoading(false);
        }
    }, [axiosInstance, applyDefaultSelection, isAuthenticated]);

    useEffect(() => {
        const preferFirst = sessionStorage.getItem(SESSION_KEYS.SELECT_FIRST_SERVER) === '1';
        if (preferFirst) {
            sessionStorage.removeItem(SESSION_KEYS.SELECT_FIRST_SERVER);
        }
        refreshConfig(preferFirst);
    }, [location.pathname, refreshConfig]);

    const setSelectedServerId = (serverId: string) => {
        setSelectedServerIdState(serverId);
        sessionStorage.setItem(SESSION_KEYS.SELECTED_SERVER_ID, serverId);
    };

    const effectiveServerId = servers.some((s) => s.serverId === selectedServerId)
        ? selectedServerId
        : servers[0]?.serverId ?? '';

    const selectedServer = servers.find((s) => s.serverId === effectiveServerId) ?? null;

    return (
        <ServerContext.Provider
            value={{
                servers,
                selectedServer,
                selectedServerId: effectiveServerId,
                setSelectedServerId,
                loading,
                refreshConfig,
                supportRefreshInterval,
                websocketUrl,
                authenticatedWebSocketUrl,
            }}
        >
            {children}
        </ServerContext.Provider>
    );
};

export const useServerContext = () => useContext(ServerContext);
